import { type ServiceCostTone } from '@/content/services';

/**
 * The palette token each cost signal borrows its colour from.
 *
 * A map rather than three `sx` blocks, for the reason `alertTone` in
 * `theme/components.ts` gives: a tint composed from a token's `mainChannel`
 * follows that token into dark mode on its own, where a hand-mixed colour has
 * to be written twice and is wrong in one of the two the first time a token
 * moves.
 *
 * The three tones are a commercial position, not a severity — "free" is not
 * success and "quoted" is not a warning. They are mapped onto palette slots
 * only because those slots are the three inks the theme already guarantees
 * contrast for at both schemes.
 */
export const costToneToken: Record<ServiceCostTone, 'success' | 'info' | 'primary'> = {
  free: 'success',
  quoted: 'info',
  deposit: 'primary',
};

/**
 * The chip's surface, edge and ink, from one token.
 *
 * ## Why the fill is paper and not a tint of the ink
 *
 * `alertTone` in `theme/components.ts` fills at 0.12 of its own token, and
 * this did too until it was measured. The light brand red is #C53D49, which
 * is 5.07:1 on white — AA for text, with 0.57 of headroom. Tinting the
 * surface behind it with 12% of that same red spends all of the headroom and
 * more: the deposit chip measured **4.05:1** in the browser, a fail at 12px,
 * and it failed on the one chip that appears on the most pages.
 *
 * An alert can afford that trade because its fill is doing real work — it
 * separates a block of warning prose from the page. A chip is four words
 * inside its own border, where the border already separates it, so the fill
 * was buying nothing and costing the contrast. On paper the ink is the
 * audited 5.07:1 (light) and ~6.2:1 (dark) and the colour coding is intact,
 * carried by the ink and the edge.
 *
 * The edge keeps `alertTone`'s 0.38, lifted slightly because it is now the
 * only thing drawing the pill's shape.
 */
export const costChipSx = (tone: ServiceCostTone) => {
  const token = costToneToken[tone];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    px: 2.5,
    py: 0.75,
    borderRadius: 999,
    border: 1,
    borderColor: `rgba(var(--mui-palette-${token}-mainChannel) / 0.45)`,
    bgcolor: 'background.paper',
    color: `var(--mui-palette-${token}-main)`,
    fontWeight: 700,
    letterSpacing: '0.01em',
    // Never wrap to two lines inside a card's footer: the labels are three
    // words at most, and a wrapped chip reads as a broken pill rather than as
    // a long one.
    whiteSpace: 'nowrap',
  } as const;
};
